import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CustomCursor from '@/pages/home/components/CustomCursor';

const sections = [
  {
    title: '1. General Policy',
    content: [
      'This Refund Policy outlines the conditions under which Syntera Solutions LTD ("Company", "we", "our", or "us") may provide refunds for payments made for services offered through this website (the "Site").',
      'We aim to provide high-quality services and fair treatment to all users. Refunds are considered in accordance with the terms set out below.',
    ],
  },
  {
    title: '2. Eligibility for Refund',
    content: [
      'Refunds may be granted under the following conditions:',
      'A payment was made directly through the Company (e.g., via the Site using a valid payment method)',
      'A verified issue occurred that materially affected the delivery of the service',
      'The refund request is submitted within 14 days of the original payment date',
      'Refunds are not guaranteed and are assessed on a case-by-case basis.',
      'Refunds will generally not be granted in the following situations:',
      'Change of mind or general dissatisfaction',
      'Failure to use the service',
      'User error or misunderstanding of the service',
      'Issues caused by third-party services or external factors beyond our control',
      'Requests submitted after the applicable refund period',
    ],
  },
  {
    title: '3. Refund Request Process',
    content: [
      'To request a refund, you must contact our support team:',
      'Email: help@syntera-solutions.com',
      'Phone: +44 7520 640897',
      'Your request should include:',
      'Full name and contact details',
      'Proof of payment (date, amount, transaction reference if available)',
      'Description of the issue or reason for the request',
      'We may request additional information to verify your claim.',
    ],
  },
  {
    title: '4. Review and Approval',
    content: [
      'All refund requests are reviewed individually.',
      'The Company reserves the right to:',
      'Approve or decline a refund request',
      'Request additional documentation',
      'Determine whether the issue qualifies under this Policy',
    ],
  },
  {
    title: '5. Processing of Refunds',
    content: [
      'If a refund is approved:',
      'The refund will be issued using the original payment method where possible',
      'Processing time may take 5–14 business days, depending on the payment provider',
      'Any applicable fees or taxes may be included where required by law',
    ],
  },
  {
    title: '6. Cancellations',
    content: [
      'If the Company offers services that can be cancelled:',
      'Cancellation prevents future charges (if applicable)',
      'Cancellation does not automatically entitle the user to a refund',
      'Refunds remain subject to the conditions outlined in this Policy.',
    ],
  },
  {
    title: '7. Limitation of Liability',
    content: [
      'The Company is not responsible for:',
      'Issues caused by user devices, software, or internet connectivity',
      'Interruptions caused by third-party services',
      'Factors outside the Company’s reasonable control',
    ],
  },
  {
    title: '8. Changes to this Policy',
    content: [
      'We reserve the right to modify this Refund Policy at any time.',
      'Updated versions will be posted on this page with a revised effective date. Continued use of the Site constitutes acceptance of the updated Policy.',
    ],
  },
  {
    title: '9. Contact Information',
    content: [
      'Syntera Solutions LTD',
      'International House',
      '64 Nile Street',
      'London',
      'N1 7SR',
      'United Kingdom',
      'Email: help@syntera-solutions.com',
      'Phone: +44 7520 640897',
    ],
  },
];

export default function RefundPage() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <main className="w-full overflow-x-hidden bg-[#0d0d0d]">
      <CustomCursor />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[60vh] flex items-end overflow-hidden pt-24">
        <span
          className="boldonse absolute select-none pointer-events-none"
          style={{
            fontSize: '40vw',
            lineHeight: 1,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -48%)',
            color: 'rgba(255,255,255,0.03)',
            letterSpacing: '-8px',
            whiteSpace: 'nowrap',
          }}
        >
          REFUND
        </span>

        <div className="relative z-10 px-10 lg:px-20 pt-32 pb-16 max-w-3xl">
          <div
            className={`transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <p className="text-[#F8CA9D] text-xs tracking-[0.5em] uppercase mb-8 font-medium">
              &#8212;&nbsp;&nbsp;Legal information&nbsp;&nbsp;&#8212;
            </p>
            <h1
              className="boldonse text-white leading-none mb-8"
              style={{ fontSize: 'clamp(56px, 8vw, 120px)', lineHeight: 0.9 }}
            >
              REFUND<br />
              <span style={{ color: '#FA6E4F' }}>POLICY.</span>
            </h1>
          </div>
          <div
            className={`transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <p className="text-white/60 text-lg leading-relaxed max-w-md">
              This policy outlines the conditions under which refunds may be granted for services purchased through our platform.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="px-10 lg:px-20 py-24 max-w-4xl mx-auto">
        <div className="flex flex-col gap-16">
          {sections.map((section, i) => (
            <div
              key={section.title}
              className={`transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${600 + i * 80}ms` }}
            >
              <h2 className="boldonse text-white text-2xl mb-6">{section.title}</h2>
              <div className="flex flex-col gap-3">
                {section.content.map((line, j) => (
                  <p
                    key={j}
                    className="text-white/70 text-base leading-relaxed whitespace-pre-line"
                  >
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
}